import { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  Calculator,
  CheckCircle2,
  MessageCircle,
  Quote,
  Star,
} from "lucide-react";
import "./App.css";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Input } from "./components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Slider } from "./components/ui/slider";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./components/ui/accordion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./components/ui/carousel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE_URL = `${BACKEND_URL}/api`;
const WEBAPP_URL = process.env.REACT_APP_WEBAPP_URL || process.env.WEBAPP_URL || "#";
const LOGO_IMAGE_URL = "https://getmalas.com.br/static/webapp/img/logo-getmalas.svg";
const MIN_DAYS = 7;
const MAX_DAYS = 30;
const GETMALAS_ASSET_BASE_URL = "https://getmalas.com.br";

const FALLBACK_BAG_OPTIONS = [
  { value: "P", label: "Mala P (10kg)" },
  { value: "M", label: "Mala M (23kg)" },
  { value: "G", label: "Mala G (32kg)" },
];
const BAG_DISPLAY_ORDER = ["P", "M", "G"];

const BENEFITS = [
  {
    iconSrc: `${GETMALAS_ASSET_BASE_URL}/static/webapp/v4/icon-economia.svg`,
    iconAlt: "Economia do aluguel de malas",
    title: "Economia que cabe no seu roteiro",
    description: "Você paga apenas pelos dias de uso e pode economizar muito mais do que comprando uma mala nova para usar poucas vezes.",
    testid: "benefit-economia-real",
  },
  {
    iconSrc: `${GETMALAS_ASSET_BASE_URL}/static/webapp/v4/icon-house.svg?2`,
    iconAlt: "Mais espaço em casa",
    title: "Mais espaço em casa",
    description: "Nada de mala ocupando armário o ano inteiro. Você recebe quando precisa e devolve após a viagem.",
    testid: "benefit-malas-premium",
  },
  {
    iconSrc: `${GETMALAS_ASSET_BASE_URL}/static/webapp/v4/icon-praticidade.svg`,
    iconAlt: "Praticidade aluguel de malas",
    title: "Praticidade do início ao fim",
    description: "Reserve online em poucos minutos e escolha a forma mais conveniente para receber e devolver suas malas.",
    testid: "benefit-consumo-inteligente",
  },
  {
    iconSrc: `${GETMALAS_ASSET_BASE_URL}/static/webapp/v4/icon-qualidade.svg`,
    iconAlt: "Malas de viagem de qualidade",
    title: "Qualidade premium garantida",
    description: "Viaje com modelos modernos, resistentes e higienizados com rigor para você ter mais conforto e segurança.",
    testid: "benefit-qualidade-premium",
  },
  {
    iconSrc: `${GETMALAS_ASSET_BASE_URL}/static/webapp/v4/icon-seguro.svg`,
    iconAlt: "Alugar malas é seguro",
    title: "Segurança e suporte sem dor de cabeça",
    description: "Se acontecer algum imprevisto com a bagagem durante a viagem, nosso time orienta você com atendimento rápido.",
    testid: "benefit-seguranca-suporte",
  },
  {
    iconSrc: `${GETMALAS_ASSET_BASE_URL}/static/webapp/v4/icon-sustentabilidade.svg`,
    iconAlt: "Economia circular e sustentabilidade",
    title: "Escolha sustentável e inteligente",
    description: "Ao alugar, você reduz consumo desnecessário e contribui para um jeito mais consciente de viajar.",
    testid: "benefit-escolha-sustentavel",
  },
];

const TESTIMONIALS = [
  {
    name: "Camila Rocha",
    city: "São Paulo • SP",
    quote:
      "Eu precisava de mala maior para uma viagem internacional e não queria gastar com compra. A GetMalas entregou tudo impecável, pontual e com atendimento excelente.",
    testid: "testimonial-camila-rocha",
  },
  {
    name: "Rafael Menezes",
    city: "Belo Horizonte • MG",
    quote:
      "Foi minha primeira experiência alugando malas e me surpreendi. O processo é simples, o custo-benefício é ótimo e a mala chegou novinha para viajar tranquilo.",
    testid: "testimonial-rafael-menezes",
  },
  {
    name: "Juliana Freitas",
    city: "Rio de Janeiro • RJ",
    quote:
      "Além de economizar, ganhei espaço em casa. Como viajo pouco, prefiro alugar com a GetMalas porque sei que posso confiar na qualidade.",
    testid: "testimonial-juliana-freitas",
  },
];

const CLIENT_GALLERY = [
  {
    src: "https://images.unsplash.com/photo-1741615171144-a50cd53f4d7f?crop=entropy&cs=srgb&fm=jpg&q=85&h=400",
    alt: "Viajantes com malas em terminal de aeroporto",
    caption: "Clientes em embarque com malas prontas para a viagem",
    testid: "client-gallery-image-1",
  },
  {
    src: "https://images.unsplash.com/photo-1537123144747-e43eff2e1040?crop=entropy&cs=srgb&fm=jpg&q=85&h=400",
    alt: "Cliente em aeroporto puxando mala de viagem",
    caption: "Praticidade no dia a dia de quem viaja",
    testid: "client-gallery-image-2",
  },
  {
    src: "https://images.unsplash.com/photo-1771970574223-24e53a0c5a24?crop=entropy&cs=srgb&fm=jpg&q=85&h=400",
    alt: "Pessoas circulando com malas em aeroporto moderno",
    caption: "Confiança de quem já escolhe alugar suas malas",
    testid: "client-gallery-image-4",
  },
  {
    src: "https://images.unsplash.com/photo-1708403120467-1715bb6840df?crop=entropy&cs=srgb&fm=jpg&q=85&h=400",
    alt: "Mala moderna em terminal de aeroporto",
    caption: "Malas modernas prontas para acompanhar cada destino",
    testid: "client-gallery-image-5",
  },
  {
    src: "https://images.unsplash.com/photo-1773228972271-41d02886869b?crop=entropy&cs=srgb&fm=jpg&q=85&h=400",
    alt: "Mala posicionada em aeroporto antes do embarque",
    caption: "Qualidade e estilo em cada detalhe da viagem",
    testid: "client-gallery-image-6",
  },
  {
    src: "https://images.unsplash.com/photo-1772751320776-fb98dfba127a?crop=entropy&cs=srgb&fm=jpg&q=85&h=400",
    alt: "Viajante aguardando com mala no saguão do aeroporto",
    caption: "Praticidade para viagens a trabalho e lazer",
    testid: "client-gallery-image-7",
  },
  {
    src: "https://images.unsplash.com/photo-1652855637945-4fa605c95190?crop=entropy&cs=srgb&fm=jpg&q=85&h=400",
    alt: "Cliente com bagagem aguardando embarque",
    caption: "Experiência segura do check-in até o retorno",
    testid: "client-gallery-image-8",
  },
];

const isSafeHttpUrl = (value) => /^https?:\/\//i.test(String(value || "").trim());

const toTestIdSlug = (value) =>
  String(value || "faq")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const sanitizeFaqHtml = (rawHtml) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(String(rawHtml || ""), "text/html");

  doc.querySelectorAll("script,style,iframe,object,embed,meta,link").forEach((node) => node.remove());

  const allowedTags = new Set(["B", "STRONG", "I", "EM", "BR", "P", "DIV", "UL", "OL", "LI", "A", "SPAN"]);

  Array.from(doc.body.querySelectorAll("*")).forEach((node) => {
    const tagName = node.tagName.toUpperCase();

    if (!allowedTags.has(tagName)) {
      const fragment = document.createDocumentFragment();
      while (node.firstChild) {
        fragment.appendChild(node.firstChild);
      }
      node.replaceWith(fragment);
      return;
    }

    Array.from(node.attributes).forEach((attribute) => {
      const attrName = attribute.name.toLowerCase();

      if (tagName === "A") {
        const allowedAnchorAttrs = ["href", "title", "target", "rel"];
        if (!allowedAnchorAttrs.includes(attrName)) {
          node.removeAttribute(attribute.name);
        }
      } else {
        node.removeAttribute(attribute.name);
      }
    });

    if (tagName === "A") {
      const href = node.getAttribute("href") || "";
      if (!isSafeHttpUrl(href)) {
        node.removeAttribute("href");
      } else {
        node.setAttribute("target", "_blank");
        node.setAttribute("rel", "noopener noreferrer");
      }
    }
  });

  return doc.body.innerHTML.trim();
};

const STEPS = [
  {
    title: "1. Escolha",
    description:
      "Online e bem simples. Você escolhe o modelo, a cor, faz a reserva e nós preparamos a sua mala. A GetMalas não possui lojas e o atendimento é feito em horários agendados.",
    image: `${GETMALAS_ASSET_BASE_URL}/static/site/images/como-funciona-passo-1.png`,
    alt: "Aluguel de malas reservar pelo site",
  },
  {
    title: "2. Receba",
    description:
      "Retire a mala com um parceiro ou receba via delivery em seu endereço. No fechamento da reserva você escolherá como quer receber a mala e em quais horários.",
    image: `${GETMALAS_ASSET_BASE_URL}/static/site/images/como-funciona-passo-2.png`,
    alt: "Locadora de malas receber em casa",
  },
  {
    title: "3. Curta!",
    description:
      "Aproveite o passeio sem dores de cabeça. Nossas malas passam por rigorosos processos de higienização. Em caso de danos ou avarias no aeroporto, nós cuidamos de tudo.",
    image: `${GETMALAS_ASSET_BASE_URL}/static/site/images/como-funciona-passo-3.png`,
    alt: "Como alugar malas seguro",
  },
  {
    title: "4. Devolva",
    description:
      "Pronto! Você acaba de ganhar mais espaço em casa, livre de produtos sem utilidade. Vamos buscar a mala em seu endereço ou você devolve no mesmo local onde retirou.",
    image: `${GETMALAS_ASSET_BASE_URL}/static/site/images/como-funciona-passo-4.png`,
    alt: "Como funciona o aluguel de malas devolucao",
  },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const normalizeRates = (rates) =>
  rates
    .map((rate) => ({
      size: String(rate.size || "").toUpperCase(),
      days: Number(rate.days),
      price: Number(rate.price),
      label: String(rate.label || "").trim(),
    }))
    .filter(
      (rate) =>
        BAG_DISPLAY_ORDER.includes(rate.size) &&
        Number.isFinite(rate.days) &&
        Number.isFinite(rate.price) &&
        rate.label.length > 0,
    )
    .sort((a, b) => BAG_DISPLAY_ORDER.indexOf(a.size) - BAG_DISPLAY_ORDER.indexOf(b.size));

const getBagLabelBySize = (size) =>
  FALLBACK_BAG_OPTIONS.find((option) => option.value === size)?.label || `Mala ${size}`;

export default function App() {
  const [bagSize, setBagSize] = useState("M");
  const [days, setDays] = useState(MIN_DAYS);
  const [rates, setRates] = useState([]);
  const [isRatesLoading, setIsRatesLoading] = useState(false);
  const [ratesError, setRatesError] = useState("");
  const [faqTopics, setFaqTopics] = useState([]);
  const [isFaqLoading, setIsFaqLoading] = useState(false);
  const [faqError, setFaqError] = useState("");

  useEffect(() => {
    const controller = new AbortController();

    const loadRates = async () => {
      setIsRatesLoading(true);
      setRatesError("");

      try {
        const response = await fetch(`${API_BASE_URL}/rates?days=${days}`, {
          method: "GET",
          signal: controller.signal,
          headers: { Accept: "application/json" },
        });

        if (!response.ok) {
          throw new Error(`Erro ao consultar tarifas (${response.status})`);
        }

        const payload = await response.json();
        const ratesPayload = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.rates)
            ? payload.rates
            : [];

        const normalized = normalizeRates(ratesPayload);
        if (!normalized.length) {
          throw new Error("Nenhuma tarifa disponível para este período");
        }

        setRates(normalized);
        if (!normalized.some((rate) => rate.size === bagSize)) {
          setBagSize(normalized[0].size);
        }
      } catch (error) {
        const errorMessage = String(error?.message || "").toLowerCase();
        if (error?.name === "AbortError" || controller.signal.aborted || errorMessage.includes("aborted")) {
          return;
        }
        setRates([]);
        setRatesError("Não foi possível atualizar as tarifas agora. Tente novamente em instantes.");
      } finally {
        if (!controller.signal.aborted) {
          setIsRatesLoading(false);
        }
      }
    };

    loadRates();
    return () => controller.abort();
  }, [days]);

  useEffect(() => {
    const controller = new AbortController();

    const loadFaq = async () => {
      setIsFaqLoading(true);
      setFaqError("");

      try {
        const response = await fetch(`${API_BASE_URL}/faq`, {
          method: "GET",
          signal: controller.signal,
          headers: { Accept: "application/json" },
        });

        if (!response.ok) {
          throw new Error(`Erro ao consultar FAQ (${response.status})`);
        }

        const payload = await response.json();
        const topics = Array.isArray(payload?.help_topics) ? payload.help_topics : [];

        const normalizedTopics = topics
          .map((topic, index) => {
            const title = String(topic?.title || "").trim();
            const content = String(topic?.content || "").trim();
            const url = String(topic?.url || "").trim();

            if (!title || !content) {
              return null;
            }

            return {
              id: `faq-topic-${index + 1}`,
              testIdSlug: `faq-topic-${toTestIdSlug(title || `item-${index + 1}`)}-${index + 1}`,
              title,
              formattedContent: sanitizeFaqHtml(content),
              sourceUrl: isSafeHttpUrl(url) ? url : "",
            };
          })
          .filter(Boolean);

        if (!normalizedTopics.length) {
          throw new Error("Nenhuma dúvida disponível no momento");
        }

        setFaqTopics(normalizedTopics);
      } catch (error) {
        const errorMessage = String(error?.message || "").toLowerCase();
        if (error?.name === "AbortError" || controller.signal.aborted || errorMessage.includes("aborted")) {
          return;
        }
        setFaqTopics([]);
        setFaqError("Não foi possível carregar as dúvidas agora. Tente novamente em instantes.");
      } finally {
        if (!controller.signal.aborted) {
          setIsFaqLoading(false);
        }
      }
    };

    loadFaq();
    return () => controller.abort();
  }, []);

  const bagOptions = rates.length
    ? rates.map((rate) => ({ value: rate.size, label: rate.label }))
    : FALLBACK_BAG_OPTIONS;

  const selectedRate = useMemo(
    () => rates.find((rate) => rate.size === bagSize),
    [rates, bagSize],
  );
  const selectedBagLabel = selectedRate?.label || getBagLabelBySize(bagSize);
  const total = selectedRate?.price ?? 0;
  const totalLabel = selectedRate ? formatCurrency(total) : "--";

  const handleDaysInput = (event) => {
    const parsed = Number(event.target.value);
    if (Number.isNaN(parsed)) {
      setDays(MIN_DAYS);
      return;
    }
    setDays(Math.max(MIN_DAYS, Math.min(MAX_DAYS, parsed)));
  };

  const goToSimulator = () => {
    document.getElementById("simulador")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="app-root" data-testid="getmalas-landing-root">
      <div className="floating-bg-orb floating-bg-orb-one" aria-hidden="true" />
      <div className="floating-bg-orb floating-bg-orb-two" aria-hidden="true" />

      <header
        className="sticky top-0 z-50 border-b border-[#d5e9e2]/90 bg-[#fffdf9]/90 backdrop-blur-md"
        data-testid="main-header"
      >
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <a
            href="#inicio"
            className="inline-flex items-center"
            data-testid="brand-logo-link"
          >
            <img
              src={LOGO_IMAGE_URL}
              alt="Logo GetMalas"
              className="h-12 w-auto object-contain"
              data-testid="brand-logo-image"
            />
            <span className="sr-only">GetMalas</span>
          </a>

          <nav className="hidden items-center gap-6 md:flex" data-testid="header-navigation">
            <a href="#beneficios" className="nav-link" data-testid="nav-beneficios-link">
              Benefícios
            </a>
            <a href="#como-funciona" className="nav-link" data-testid="nav-como-funciona-link">
              Como funciona
            </a>
            <a href="#duvidas" className="nav-link" data-testid="nav-duvidas-link">
              Dúvidas
            </a>
            <a href="#simulador" className="nav-link" data-testid="nav-precos-link">
              Preços
            </a>
          </nav>

          <Button asChild className="cta-primary-btn rounded-full px-5" data-testid="header-whatsapp-button">
            <a href={WEBAPP_URL} rel="noreferrer" data-testid="header-whatsapp-link">
              Escolher mala
            </a>
          </Button>
        </div>
      </header>

      <main id="inicio" data-testid="main-content">
        <section className="mx-auto grid w-full max-w-7xl gap-10 px-4 pb-14 pt-10 sm:px-6 md:grid-cols-2 lg:px-8 lg:pt-14">
          <div className="reveal-up" data-testid="hero-content-block" style={{ animationDelay: "0.1s" }}>
            <p className="mb-4 inline-flex items-center rounded-full bg-[#e7f8f4] px-4 py-2 text-xs font-semibold text-[#0F7A7A]" data-testid="hero-pill-text">
              Consumo consciente para viagens sem excessos
            </p>
            <h1 className="font-heading text-4xl font-bold leading-tight text-slate-900 sm:text-5xl lg:text-6xl" data-testid="hero-main-heading">
              Guarde só as lembranças felizes do passeio.
            </h1>
            <p className="mt-5 max-w-xl text-sm text-slate-600 sm:text-base" data-testid="hero-subheading-text">
             Viaje com malas Premium e viva o melhor de cada destino com a GetMalas.
            </p>

            <div className="mt-8 flex flex-wrap gap-3" data-testid="hero-cta-group">
              <Button
                className="cta-primary-btn rounded-full px-7 py-6 text-base"
                onClick={goToSimulator}
                data-testid="hero-simular-button"
              >
                Simular aluguel
                <Calculator className="ml-2 h-4 w-4" />
              </Button>
              <Button asChild variant="outline" className="cta-outline-btn rounded-full px-7 py-6 text-base" data-testid="hero-whatsapp-secondary-button">
                <a href={WEBAPP_URL} rel="noreferrer" data-testid="hero-whatsapp-secondary-link">
                  Escolher mala
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>

            <div className="mt-8 flex flex-wrap gap-5 text-sm text-slate-600" data-testid="hero-trust-list">
              <span className="inline-flex items-center gap-2" data-testid="hero-trust-item-limpeza">
                <span className="h-2 w-2 rounded-full bg-[#20BF9A]" />
                Higienização profissional
              </span>
              <span className="inline-flex items-center gap-2" data-testid="hero-trust-item-entrega">
                <span className="h-2 w-2 rounded-full bg-[#20BF9A]" />
                Entrega e coleta facilitadas
              </span>
            </div>
          </div>

          <div className="reveal-up" data-testid="hero-visual-block" style={{ animationDelay: "0.25s" }}>
            <div className="hero-visual-wrap" data-testid="hero-illustration-container">
              <img
                src="https://images.unsplash.com/photo-1708403120467-1715bb6840df?crop=entropy&cs=srgb&fm=jpg&q=85&h=750"
                alt="Mala de viagem em ambiente de aeroporto"
                className="hero-image"
                data-testid="hero-main-image"
              />
              <div className="hero-shape hero-shape-one" data-testid="hero-shape-one" />
              <div className="hero-shape hero-shape-two" data-testid="hero-shape-two" />
              <div className="hero-floating-card hero-floating-card-top" data-testid="hero-floating-card-price">
                <p className="text-xs text-slate-500" data-testid="hero-floating-card-price-label">
                  A partir de
                </p>
                <p className="font-heading text-lg font-semibold text-slate-900" data-testid="hero-floating-card-price-value">
                  R$89,90
                </p>
              </div>
              <div className="hero-floating-card hero-floating-card-bottom" data-testid="hero-floating-card-speed">
                <MessageCircle className="h-4 w-4 text-[#0F7A7A]" />
                <p className="text-xs font-medium text-slate-700" data-testid="hero-floating-card-speed-value">
                  Orçamento em minutos
                </p>
              </div>
            </div>
          </div>
        </section>

        <section id="beneficios" className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="benefits-section">
          <div className="mb-7 reveal-up" style={{ animationDelay: "0.15s" }}>
            <h2 className="font-heading text-3xl font-bold text-slate-900 sm:text-4xl" data-testid="benefits-heading">
              Vantagens exclusivas de alugar suas malas
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-slate-600 sm:text-base" data-testid="benefits-subheading">
              Você já investe em passagens, hospedagem e experiências. Com a GetMalas, a bagagem também vira um ponto inteligente da viagem: menos gasto, mais comodidade e mais confiança em cada embarque.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="benefits-grid">
            {BENEFITS.map((benefit, index) => {
              return (
                <Card key={benefit.title} className="reveal-up border-slate-100 shadow-[0_8px_30px_rgba(15,23,42,0.05)]" style={{ animationDelay: `${0.2 + index * 0.1}s` }} data-testid={benefit.testid}>
                  <CardContent className="p-6">
                    <div className="mb-4 inline-flex rounded-full bg-[#e7f8f4] p-3" data-testid={`${benefit.testid}-icon-wrap`}>
                      <img
                        src={benefit.iconSrc}
                        alt={benefit.iconAlt}
                        width="50"
                        height="50"
                        className="h-[50px] w-[50px] object-contain"
                        data-testid={`${benefit.testid}-icon`}
                      />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-slate-900" data-testid={`${benefit.testid}-title`}>
                      {benefit.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-600" data-testid={`${benefit.testid}-description`}>
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="testimonials-section">
          <div className="mb-7 reveal-up" style={{ animationDelay: "0.18s" }}>
            <h2 className="font-heading text-3xl font-bold text-slate-900 sm:text-4xl" data-testid="testimonials-heading">
              Depoimentos de clientes
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-slate-600 sm:text-base" data-testid="testimonials-subheading">
              Opiniões de quem já trocou a compra de malas por uma experiência mais prática, econômica e confiável.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-3" data-testid="testimonials-grid">
            {TESTIMONIALS.map((item, index) => (
              <Card
                key={item.name}
                className="reveal-up border-slate-100 bg-white shadow-[0_8px_30px_rgba(15,23,42,0.05)]"
                style={{ animationDelay: `${0.2 + index * 0.1}s` }}
                data-testid={item.testid}
              >
                <CardContent className="p-6">
                  <div className="mb-4 flex items-center justify-between" data-testid={`${item.testid}-header`}>
                    <div className="flex items-center gap-1" data-testid={`${item.testid}-rating`}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} className="h-4 w-4 fill-[#e91e63] text-[#e91e63]" data-testid={`${item.testid}-star-${star}`} />
                      ))}
                    </div>
                    <Quote className="h-5 w-5 text-[#0F7A7A]" data-testid={`${item.testid}-quote-icon`} />
                  </div>
                  <p className="text-sm leading-relaxed text-slate-700" data-testid={`${item.testid}-quote-text`}>
                    “{item.quote}”
                  </p>
                  <div className="mt-5 border-t border-slate-100 pt-4" data-testid={`${item.testid}-author-block`}>
                    <p className="font-heading text-lg font-semibold text-slate-900" data-testid={`${item.testid}-author-name`}>
                      {item.name}
                    </p>
                    <p className="text-xs uppercase tracking-[0.16em] text-slate-500" data-testid={`${item.testid}-author-city`}>
                      {item.city}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="client-gallery-section">
          <div className="mb-7 reveal-up" style={{ animationDelay: "0.22s" }}>
            <h2 className="font-heading text-3xl font-bold text-slate-900 sm:text-4xl" data-testid="client-gallery-heading">
              Aproveite a experiência
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-slate-600 sm:text-base" data-testid="client-gallery-subheading">
              Nossas malas facilitam a sua viagem em diferentes rotas e estilos de embarque.
            </p>
          </div>

          <div className="reveal-up px-1 sm:px-10" style={{ animationDelay: "0.24s" }} data-testid="client-gallery-carousel-wrap">
            <Carousel
              opts={{ align: "start", loop: true }}
              className="w-full"
              data-testid="client-gallery-carousel"
            >
              <CarouselContent data-testid="client-gallery-carousel-content">
                {CLIENT_GALLERY.map((item) => (
                  <CarouselItem key={item.testid} className="basis-[86%] sm:basis-1/2 lg:basis-1/3" data-testid={`${item.testid}-slide`}>
                    <figure
                      className="overflow-hidden rounded-2xl border border-slate-100 bg-white shadow-[0_8px_30px_rgba(15,23,42,0.05)]"
                      data-testid={item.testid}
                    >
                      <div className="aspect-[4/3]" data-testid={`${item.testid}-image-wrap`}>
                        <img
                          src={item.src}
                          alt={item.alt}
                          className="h-full w-full object-cover object-center"
                          data-testid={`${item.testid}-image`}
                        />
                      </div>
                      <figcaption className="px-4 py-3 text-sm text-slate-700" data-testid={`${item.testid}-caption`}>
                        {item.caption}
                      </figcaption>
                    </figure>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious
                className="left-1 top-1/2 border-[#e8c3d2] bg-white text-[#e91e63] hover:bg-[#fff3f8] sm:left-2"
                data-testid="client-gallery-prev-button"
                aria-label="Imagem anterior"
              />
              <CarouselNext
                className="right-1 top-1/2 border-[#e8c3d2] bg-white text-[#e91e63] hover:bg-[#fff3f8] sm:right-2"
                data-testid="client-gallery-next-button"
                aria-label="Próxima imagem"
              />
            </Carousel>
          </div>
        </section>

        <section id="como-funciona" className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="how-it-works-section">
          <div className="mb-8 reveal-up" style={{ animationDelay: "0.2s" }}>
            <h2 className="font-heading text-3xl font-bold text-slate-900 sm:text-4xl" data-testid="how-it-works-heading">
              Como funciona
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-slate-600 sm:text-base" data-testid="how-it-works-subheading">
              Conheça o passo a passo para reservar, receber, curtir e devolver sua mala.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="how-it-works-grid">
            {STEPS.map((step, index) => {
              return (
                <div key={step.title} className="reveal-up rounded-2xl border border-slate-100 bg-white p-5 shadow-[0_8px_26px_rgba(15,23,42,0.04)]" style={{ animationDelay: `${0.25 + index * 0.08}s` }} data-testid={`how-step-${index + 1}`}>
                  <div className="mb-4 flex min-h-[190px] items-center justify-center" data-testid={`how-step-${index + 1}-image-wrap`}>
                    <img
                      src={step.image}
                      alt={step.alt}
                      className="h-auto w-[200px] max-w-full object-contain"
                      data-testid={`how-step-${index + 1}-image`}
                    />
                  </div>
                  <h3 className="mt-2 font-heading text-lg font-semibold text-slate-900" data-testid={`how-step-${index + 1}-title`}>
                    {step.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600" data-testid={`how-step-${index + 1}-description`}>
                    {step.description}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        <section id="simulador" className="mx-auto w-full max-w-7xl px-4 pb-14 pt-10 sm:px-6 lg:px-8" data-testid="simulator-section">
          <div className="grid gap-8 lg:grid-cols-[1.05fr_1fr]">
            <div className="reveal-up" style={{ animationDelay: "0.25s" }} data-testid="simulator-intro-block">
              <h2 className="font-heading text-3xl font-bold text-slate-900 sm:text-4xl" data-testid="simulator-heading">
                Calculadora de tarifas
              </h2>
              <p className="mt-3 max-w-xl text-sm text-slate-600 sm:text-base" data-testid="simulator-subheading">
                Selecione o tamanho e a quantidade de diárias (mínimo de 7) para calcular pelo endpoint oficial em tempo real.
              </p>
            </div>

            <Card className="reveal-up border-slate-100 bg-white shadow-[0_14px_34px_rgba(15,23,42,0.09)]" style={{ animationDelay: "0.32s" }} data-testid="tariff-simulator-card">
              <CardContent className="space-y-6 p-6 sm:p-8">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700" data-testid="bag-size-label" htmlFor="bag-size-trigger">
                    Tamanho da mala
                  </label>
                  <Select value={bagSize} onValueChange={setBagSize}>
                    <SelectTrigger id="bag-size-trigger" className="h-12 rounded-xl border-slate-200 bg-slate-50" data-testid="bag-size-select-trigger">
                      <SelectValue placeholder="Selecione o tamanho" data-testid="bag-size-select-value" />
                    </SelectTrigger>
                    <SelectContent data-testid="bag-size-select-content">
                      {bagOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value} data-testid={`bag-size-option-${option.value.toLowerCase()}`}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="daily-number-input" data-testid="daily-number-label">
                    Número de diárias
                  </label>
                  <Input
                    id="daily-number-input"
                    type="number"
                    min={MIN_DAYS}
                    max={MAX_DAYS}
                    value={days}
                    onChange={handleDaysInput}
                    className="h-12 rounded-xl border-slate-200 bg-slate-50"
                    data-testid="daily-number-input"
                  />
                  <p className="mt-2 text-xs text-slate-500" data-testid="daily-minimum-hint">
                    Mínimo de {MIN_DAYS} diárias
                  </p>
                </div>

                <div>
                  <p className="mb-3 text-sm font-medium text-slate-700" data-testid="daily-slider-label">
                    Ajuste rápido das diárias
                  </p>
                  <Slider
                    min={MIN_DAYS}
                    max={MAX_DAYS}
                    step={1}
                    value={[days]}
                    onValueChange={(value) => setDays(Math.max(MIN_DAYS, value[0]))}
                    className="py-2"
                    data-testid="daily-slider"
                  />
                </div>

                <div className="rounded-2xl border border-[#b7e8dc] bg-[#ecfaf6] p-5" data-testid="simulation-result-box">
                  <p className="text-sm text-slate-700" data-testid="result-size-text">
                    Mala selecionada: <strong>{selectedBagLabel}</strong>
                  </p>
                  <p className="mt-1 text-sm text-slate-700" data-testid="result-daily-rate-text">
                    Diárias selecionadas: <strong>{days}</strong>
                  </p>
                  <p className="mt-3 font-heading text-3xl font-bold text-[#0F7A7A]" data-testid="result-total-price">
                    Total: {isRatesLoading ? "Atualizando..." : totalLabel}
                  </p>
                </div>

                {!isRatesLoading && ratesError && rates.length === 0 && (
                  <p className="text-sm text-rose-600" data-testid="rates-error-text">
                    {ratesError}
                  </p>
                )}

                {isRatesLoading || !selectedRate ? (
                  <Button
                    className="cta-primary-btn h-12 w-full rounded-full text-base"
                    data-testid="simulator-whatsapp-button-disabled"
                    disabled
                  >
                    Aguardando tarifas para liberar o WhatsApp
                  </Button>
                ) : (
                  <Button
                    asChild
                    className="cta-primary-btn h-12 w-full rounded-full text-base"
                    data-testid="simulator-whatsapp-button"
                  >
                    <a href={WEBAPP_URL} target="_blank" rel="noreferrer" data-testid="simulator-whatsapp-link">
                      Escolher minha mala
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                )}
              </CardContent>
            </Card>
          </div>
        </section>

        <section id="duvidas" className="mx-auto w-full max-w-7xl px-4 py-10 sm:px-6 lg:px-8" data-testid="faq-section">
          <div className="mb-7 reveal-up" style={{ animationDelay: "0.28s" }} data-testid="faq-heading-wrap">
            <h2 className="font-heading text-3xl font-bold text-slate-900 sm:text-4xl" data-testid="faq-heading">
              Dúvidas mais frequentes
            </h2>
            <p className="mt-3 max-w-2xl text-sm text-slate-600 sm:text-base" data-testid="faq-subheading">
              Reunimos as principais perguntas sobre locação, entrega, pagamento e devolução para você reservar com segurança.
            </p>
          </div>

          <div className="reveal-up rounded-2xl border border-slate-100 bg-white p-4 shadow-[0_10px_30px_rgba(15,23,42,0.05)] sm:p-6" style={{ animationDelay: "0.34s" }} data-testid="faq-accordion-wrap">
            {isFaqLoading ? (
              <p className="text-sm text-slate-600" data-testid="faq-loading-text">
                Carregando perguntas frequentes...
              </p>
            ) : null}

            {!isFaqLoading && faqError ? (
              <p className="text-sm text-rose-600" data-testid="faq-error-text">
                {faqError}
              </p>
            ) : null}

            {!isFaqLoading && !faqError && faqTopics.length > 0 ? (
              <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
                {faqTopics.map((item, index) => (
                  <AccordionItem key={item.id} value={item.id} className="border-slate-100" data-testid={`faq-item-${index + 1}`}>
                    <AccordionTrigger
                      className="text-base font-semibold text-slate-800 hover:no-underline"
                      data-testid={`${item.testIdSlug}-question`}
                    >
                      {item.title}
                    </AccordionTrigger>
                    <AccordionContent className="space-y-4" data-testid={`${item.testIdSlug}-answer`}>
                      <div
                        className="faq-html-content text-sm leading-relaxed text-slate-600"
                        data-testid={`${item.testIdSlug}-content-html`}
                        dangerouslySetInnerHTML={{ __html: item.formattedContent }}
                      />

                      {item.sourceUrl ? (
                        <a
                          href={item.sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex text-sm font-medium text-[#e91e63] hover:underline"
                          data-testid={`${item.testIdSlug}-source-link`}
                        >
                          Ver tópico completo
                        </a>
                      ) : null}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : null}
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 pb-16 sm:px-6 lg:px-8" data-testid="final-cta-section">
          <div className="cta-banner reveal-up" style={{ animationDelay: "0.35s" }}>
            <img
              src="https://images.unsplash.com/photo-1741615171144-a50cd53f4d7f?crop=entropy&cs=srgb&fm=jpg&q=65&w=1000"
              alt="Pessoas em viagem com malas modernas no aeroporto"
              className="cta-banner-image"
              data-testid="final-cta-image"
            />
            <div className="cta-overlay" />
            <div className="cta-content" data-testid="final-cta-content">
              <p className="inline-flex items-center gap-2 rounded-full bg-white/20 px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-white" data-testid="final-cta-pill">
                <CheckCircle2 className="h-4 w-4" />
                Próxima viagem sem estresse
              </p>
              <h2 className="font-heading mt-4 text-3xl font-bold text-white sm:text-4xl" data-testid="final-cta-heading">
                Pronto para garantir a mala certa para sua aventura?
              </h2>
              <p className="mt-3 max-w-2xl text-sm text-[#f8fffd] sm:text-base" data-testid="final-cta-subheading">
                Envie sua simulação e receba atendimento rápido para fechar sua locação com a GetMalas.
              </p>
              <Button asChild className="cta-primary-btn mt-6 h-12 rounded-full px-8 text-base font-semibold" data-testid="final-cta-whatsapp-button">
                <a href={WEBAPP_URL} rel="noreferrer" data-testid="final-cta-whatsapp-link">
                  Escolher mala
                  <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-[#d5e9e2] bg-[#fffefb]/90" data-testid="main-footer">
        <div className="mx-auto flex w-full max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-6 text-sm text-slate-600 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3" data-testid="footer-brand-block">
            <img
              src={LOGO_IMAGE_URL}
              alt="Logo GetMalas"
              className="h-9 w-auto object-contain"
              data-testid="footer-logo-image"
            />
            <p data-testid="footer-copyright">© {new Date().getFullYear()} GetMalas. Todos os direitos reservados.</p>
          </div>
          <p className="text-xs uppercase tracking-[0.15em] text-slate-400" data-testid="footer-slogan">
            Viaje mais • Compre menos
          </p>
        </div>
      </footer>
    </div>
  );
}
